import { useMutation, useQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { useParams } from "react-router-dom";
import Permissions from "../configs/permissions";
import usePermissions from "../hooks/permissions";
import { QUESTION_MODEL_NAME, QUESTION_TYPES } from "./../configs/constants";
import { updateQuestionMutation } from "./../graphql/mutations/vabooSurveyMutations";
import { questionDetails } from "./../graphql/queries/vabooSurveyQueries";
import useBaseFieldSetters from "./../hooks/baseFieldSetters";
import useBaseNavigation from "./../hooks/baseNavigation";
import useFormErrors from "./../hooks/formErrors";
import useHyperState from "./../hooks/hyperState";
import { useQuestionFieldSetters } from "./../hooks/vabooHooks";

import {
  AddButton,
  CancelButton,
  DeleteButton,
  FormControls,
  SaveButton,
} from "./../mood-ui/Controls";
import {
  TableContainer,
  TableView,
  TColBorderless,
  TContent,
  THCol,
  THeading,
  TRow,
} from "./../mood-ui/DataTable";
import {
  NumberField,
  ReadOnlyField,
  SelectField,
  TextField,
  ToggleCheckboxField,
} from "./../mood-ui/Fields";
import {
  BlockContainer,
  Col,
  FormContainer,
  FormData,
  PageContainer,
  Row,
} from "./../mood-ui/Layout";
import PageSpinner from "./../mood-ui/PageSpinner";
import { BoxedMessage, Heading, InfoMessage } from "./../mood-ui/Typography";

const QUESTION_DETAILS = gql(questionDetails);
const UPDATE_QUESTION = gql(updateQuestionMutation);

const Question = () => {
  const { protectPage, hasAccess } = usePermissions();
  protectPage(Permissions.Query.Question);
  const { fieldError, resolveMutationErrors, clearError } = useFormErrors();
  const { goBack } = useBaseNavigation(QUESTION_MODEL_NAME);
  const { id } = useParams();

  const {
    state: formData,
    setState: setFormData,
    stateMethods,
  } = useHyperState("formData", {
    title: "",
    is_profile_type: false,
    is_survey_type: false,
    admin_title: "",
    type: "",
    status: "Draft",
    answers: [],
  });

  const {
    setFieldValue: handleFieldOnChange,
    toggleCheckbox: handleToggleCheckbox,
  } = useBaseFieldSetters(formData, stateMethods, clearError);

  const {
    handleAddAnswer,
    handleUpdateAnswer,
    handleDeleteAnswer,
    handleUpdateType,
    handleAddDateBracketsAnswer,
    handleUpdateDateBracketsAnswerProperty,
  } = useQuestionFieldSetters(formData, stateMethods);

  const { data, loading, error } = useQuery(QUESTION_DETAILS, {
    variables: { id },
    fetchPolicy: "cache-and-network",
    onCompleted: (result) => {
      if (!result || !result.question) return;
      const answers = result.question.answers.map((answer) => {
        if (result.question.type === "DATE_BRACKETS") {
          return {
            id: answer.id,
            title: answer.title,
            minimum: answer.metadata.minimum,
            maximum: answer.metadata.maximum,
            weight: answer.metadata.weight,
          };
        }
        return { id: answer.id, title: answer.title };
      });

      const data = {
        id: result.question.id,
        status: result.question.status,
        title: result.question.title,
        admin_title: result.question.admin_title,
        type: result.question.type,
        answers,
        is_profile_type: result.question.is_profile_type,
        is_survey_type: result.question.is_survey_type,
      };

      if (data.type === "RATING" || data.type === "YEAR") {
        const params = JSON.parse(result.question.params || "{}");
        if (
          typeof params.minimum !== "undefined" &&
          typeof params.maximum !== "undefined" &&
          typeof params.step !== "undefined" &&
          typeof params.default !== "undefined"
        ) {
          data.minimum = params.minimum;
          data.maximum = params.maximum;
          data.step = params.step;
          data.default = params.default;
        }
      }

      setFormData({ ...data });
    },
  });

  const [
    updateQuestion,
    { data: updateResult, loading: isUpdating, error: updateError },
  ] = useMutation(UPDATE_QUESTION, {
    variables: { ...formData },
    onCompleted: () => {
      goBack();
    },
    onError: (e) => {
      resolveMutationErrors(e);
    },
  });

  const handleSave = () => {
    const data = { ...formData, answers: JSON.stringify(formData.answers) };
    updateQuestion({ variables: data });
  };

  const isPublished = formData && formData.status === "Published";

  if (error) return <div>Error...</div>;
  if (!formData || loading) return <PageSpinner />;

  return (
    <PageContainer>
      <BlockContainer raised>
        <Row tweaks="border-b border-[#dfe2e8] pb-8 mt-0 mx-5">
          <Col width={12}>
            <Heading
              inset
              text="Question"
              description="View or edit the question. Published questions are read-only."
            />
          </Col>
        </Row>

        <FormContainer>
          <FormData>
            {false && (
              <Row tweaks="border-b border-[#dfe2e8] py-5 mx-5">
                <Col width={12}>
                  <InfoMessage text="This question is published. Changes are not allowed." />
                </Col>
              </Row>
            )}

            <Row tweaks="border-b border-[#dfe2e8] py-7 mx-5">
              <Col width={3}>
                <Heading
                  size="md"
                  text="Question details"
                  description="Core properties and naming."
                />
              </Col>
              <Col
                width={1}
                extraStyles="border-r border-[#dfe2e8] h-full justify-self-center w-full"
              />
              <Col width={8}>
                <Row>
                  <Col width={8}>
                    {false ? (
                      <ReadOnlyField
                        value={formData.title}
                        label="Question"
                        isRequired
                      />
                    ) : (
                      <TextField
                        name="title"
                        label="Question"
                        value={formData.title}
                        onChange={handleFieldOnChange}
                        error={fieldError("title")}
                        isRequired
                        minlength={3}
                        maxlength={160}
                        description="End-user visible wording"
                      />
                    )}
                  </Col>
                  <Col width={4}>
                    <SelectField
                      value={formData.type}
                      values={QUESTION_TYPES}
                      label="Type"
                      name="type"
                      onChange={handleUpdateType}
                      valueKey="id"
                      labelKey="name"
                      error={fieldError("type")}
                      isDisabled={isPublished}
                      isRequired
                      description="Answer input format"
                    />
                  </Col>
                  <Col width={12}>
                    {false ? (
                      <ReadOnlyField
                        value={formData.admin_title}
                        label="Admin Title"
                        isRequired
                      />
                    ) : (
                      <TextField
                        name="admin_title"
                        label="Admin Title"
                        value={formData.admin_title}
                        onChange={handleFieldOnChange}
                        error={fieldError("admin_title")}
                        isRequired
                        maxlength={160}
                        description="Internal name for admin/reporting"
                      />
                    )}
                  </Col>
                </Row>
              </Col>
            </Row>

            <Row tweaks="border-b border-[#dfe2e8] py-7 mx-5">
              <Col width={3}>
                <Heading
                  size="md"
                  text="Usage"
                  description="Where this question can be reused."
                />
              </Col>
              <Col
                width={1}
                extraStyles="border-r border-[#dfe2e8] h-full justify-self-center w-full"
              />
              <Col width={8}>
                <Row tweaks="gap-0 sm:grid-cols-12 py-1 px-px">
                  <Col width={3} extraStyles="sm:col-span-2">
                    {isPublished ? (
                      <ReadOnlyField
                        value={formData.is_survey_type ? "Yes" : "No"}
                        label="Survey Usage"
                      />
                    ) : (
                      <ToggleCheckboxField
                        id="is_survey_type"
                        name="is_survey_type"
                        label="Survey Usage"
                        description="Available for surveys"
                        isChecked={formData.is_survey_type}
                        onChange={handleToggleCheckbox}
                        flush
                      />
                    )}
                  </Col>
                  <Col width={3} extraStyles="sm:col-span-2">
                    {isPublished ? (
                      <ReadOnlyField
                        value={formData.is_profile_type ? "Yes" : "No"}
                        label="Profile Usage"
                      />
                    ) : (
                      <ToggleCheckboxField
                        id="is_profile_type"
                        name="is_profile_type"
                        label="Profile Usage"
                        description="Available for profiles"
                        isChecked={formData.is_profile_type}
                        onChange={handleToggleCheckbox}
                        flush
                      />
                    )}
                  </Col>
                </Row>
              </Col>
            </Row>

            {["TEXT"].includes(formData.type) && (
              <Row tweaks="border-b border-[#dfe2e8] py-7 mx-5">
                <Col width={3}>
                  <Heading size="md" text="Answers" description="Information" />
                </Col>
                <Col
                  width={1}
                  extraStyles="border-r border-[#dfe2e8] h-full justify-self-center w-full"
                />
                <Col width={8}>
                  <BoxedMessage
                    text="Answers are not available for this type"
                    description="When using Text as question type, answers become unavailable."
                  />
                </Col>
              </Row>
            )}

            {["RATING"].includes(formData.type) && (
              <Row tweaks="border-b border-[#dfe2e8] py-7 mx-5">
                <Col width={3}>
                  <Heading
                    size="md"
                    text="Rating configuration"
                    description="Min/max, step and default rating."
                  />
                </Col>
                <Col
                  width={1}
                  extraStyles="border-r border-[#dfe2e8] h-full justify-self-center w-full"
                />
                <Col width={8}>
                  <Row>
                    <Col width={3}>
                      {isPublished ? (
                        <ReadOnlyField
                          value={formData.minimum}
                          label="Minimum Rating Value"
                        />
                      ) : (
                        <NumberField
                          name="minimum"
                          label="Minimum Rating Value"
                          value={formData.minimum}
                          onChange={(e) => handleFieldOnChange(e, "int")}
                          error={fieldError("minimum")}
                          description="Lowest allowed value"
                        />
                      )}
                    </Col>
                    <Col width={3}>
                      {isPublished ? (
                        <ReadOnlyField
                          value={formData.maximum}
                          label="Maximum Rating Value"
                        />
                      ) : (
                        <NumberField
                          name="maximum"
                          label="Maximum Rating Value"
                          value={formData.maximum}
                          onChange={(e) => handleFieldOnChange(e, "int")}
                          error={fieldError("maximum")}
                          description="Highest allowed value"
                        />
                      )}
                    </Col>
                    <Col width={3}>
                      {isPublished ? (
                        <ReadOnlyField value={formData.step} label="Step" />
                      ) : (
                        <NumberField
                          name="step"
                          label="Rating Step"
                          value={formData.step}
                          onChange={(e) => handleFieldOnChange(e, "int")}
                          error={fieldError("step")}
                          description="Increment"
                        />
                      )}
                    </Col>
                    <Col width={3}>
                      {isPublished ? (
                        <ReadOnlyField
                          value={formData.default}
                          label="Default Value"
                        />
                      ) : (
                        <NumberField
                          name="default"
                          label="Default Value"
                          value={formData.default}
                          onChange={(e) => handleFieldOnChange(e, "int")}
                          error={fieldError("default")}
                          description="Preselected rating"
                        />
                      )}
                    </Col>
                  </Row>
                </Col>
              </Row>
            )}

            {["YEAR"].includes(formData.type) && (
              <Row tweaks="border-b border-[#dfe2e8] py-7 mx-5">
                <Col width={3}>
                  <Heading
                    size="md"
                    text="Year range"
                    description="Numeric bounds and step."
                  />
                </Col>
                <Col
                  width={1}
                  extraStyles="border-r border-[#dfe2e8] h-full justify-self-center w-full"
                />
                <Col width={8}>
                  <Row>
                    <Col width={3}>
                      {isPublished ? (
                        <ReadOnlyField value={formData.minimum} label="From" />
                      ) : (
                        <NumberField
                          name="minimum"
                          label="Year (From)"
                          value={formData.minimum}
                          onChange={handleFieldOnChange}
                          error={fieldError("minimum")}
                          description="Lower bound"
                        />
                      )}
                    </Col>
                    <Col width={3}>
                      {isPublished ? (
                        <ReadOnlyField value={formData.maximum} label="To" />
                      ) : (
                        <NumberField
                          name="maximum"
                          label="Year (To)"
                          value={formData.maximum}
                          onChange={handleFieldOnChange}
                          error={fieldError("maximum")}
                          description="Upper bound"
                        />
                      )}
                    </Col>
                    <Col width={3}>
                      {isPublished ? (
                        <ReadOnlyField value={formData.step} label="Step" />
                      ) : (
                        <NumberField
                          name="step"
                          label="Year Step"
                          value={formData.step}
                          onChange={handleFieldOnChange}
                          error={fieldError("step")}
                          description="Increment"
                        />
                      )}
                    </Col>
                    <Col width={3}>
                      {isPublished ? (
                        <ReadOnlyField
                          value={formData.default}
                          label="Default Year"
                        />
                      ) : (
                        <NumberField
                          name="default"
                          label="Default Year"
                          value={formData.default}
                          onChange={handleFieldOnChange}
                          error={fieldError("default")}
                          description="Preselected year"
                        />
                      )}
                    </Col>
                  </Row>
                </Col>
              </Row>
            )}

            {["DATE_BRACKETS"].includes(formData.type) && (
              <Row tweaks="border-b border-[#dfe2e8] py-7 mx-5">
                <Col width={3}>
                  <Heading
                    size="md"
                    text="Answers"
                    description="Add ranges and their weights."
                  />
                </Col>
                <Col
                  width={1}
                  extraStyles="border-r border-[#dfe2e8] h-full justify-self-center w-full"
                />
                <Col width={8}>
                  {!isPublished && (
                    <Row>
                      <Col
                        width={12}
                        extraStyles="flex items-center gap-2 mb-3"
                      >
                        <AddButton icon onClick={handleAddDateBracketsAnswer} />
                        <span className="text-sm text-[#6b7280]">
                          Add Answer
                        </span>
                      </Col>
                    </Row>
                  )}

                  {formData.answers.length > 0 && (
                    <TableContainer tweaks="relative mt-0">
                      <TableView>
                        <THeading>
                          <TRow>
                            {isPublished && (
                              <THCol name="id" value="ID" width="w-1" />
                            )}
                            <THCol name="minimum" value="Range (From)" />
                            <THCol name="maximum" value="Range (To)" />
                            <THCol name="title" value="Range (Text)" />
                            <THCol name="weight" value="Computed Weight" />
                            {!isPublished && <THCol />}
                          </TRow>
                        </THeading>
                        <TContent>
                          {formData.answers.map((answer, index) => (
                            <TRow key={index}>
                              {isPublished && (
                                <TColBorderless>
                                  <ReadOnlyField value={answer.id} />
                                </TColBorderless>
                              )}
                              <TColBorderless tweaks="align-middle">
                                {isPublished ? (
                                  <ReadOnlyField value={answer.minimum} />
                                ) : (
                                  <NumberField
                                    value={answer.minimum}
                                    onChange={(e) =>
                                      handleUpdateDateBracketsAnswerProperty(
                                        index,
                                        "minimum",
                                        e.target.value,
                                      )
                                    }
                                  />
                                )}
                              </TColBorderless>
                              <TColBorderless tweaks="align-middle">
                                {!isPublished ? (
                                  <ReadOnlyField value={answer.maximum} />
                                ) : (
                                  <NumberField
                                    value={answer.maximum}
                                    onChange={(e) =>
                                      handleUpdateDateBracketsAnswerProperty(
                                        index,
                                        "maximum",
                                        e.target.value,
                                      )
                                    }
                                  />
                                )}
                              </TColBorderless>
                              <TColBorderless tweaks="align-middle">
                                {isPublished ? (
                                  <ReadOnlyField value={answer.title} />
                                ) : (
                                  <TextField
                                    value={answer.title}
                                    onChange={(e) =>
                                      handleUpdateDateBracketsAnswerProperty(
                                        index,
                                        "title",
                                        e.target.value,
                                      )
                                    }
                                  />
                                )}
                              </TColBorderless>
                              <TColBorderless tweaks="align-middle">
                                {isPublished ? (
                                  <ReadOnlyField value={answer.weight} />
                                ) : (
                                  <NumberField
                                    value={answer.weight}
                                    onChange={(e) =>
                                      handleUpdateDateBracketsAnswerProperty(
                                        index,
                                        "weight",
                                        e.target.value,
                                      )
                                    }
                                  />
                                )}
                              </TColBorderless>
                              {!isPublished && (
                                <TColBorderless
                                  width="w-12"
                                  tweaks="px-0.5 py-0.5 align-middle text-right"
                                >
                                  <DeleteButton
                                    icon
                                    onClick={() => handleDeleteAnswer(index)}
                                  />
                                </TColBorderless>
                              )}
                            </TRow>
                          ))}
                        </TContent>
                      </TableView>
                    </TableContainer>
                  )}
                </Col>
              </Row>
            )}

            {[
              "MULTIPLE",
              "MULTIPLE_AND_CUSTOM",
              "SINGLE",
              "SINGLE_AND_CUSTOM",
            ].includes(formData.type) && (
              <Row tweaks="border-b border-[#dfe2e8] py-7 mx-5">
                <Col width={3}>
                  <Heading
                    size="md"
                    text="Answers"
                    description="Provide selectable options."
                  />
                </Col>
                <Col
                  width={1}
                  extraStyles="border-r border-[#dfe2e8] h-full justify-self-center w-full"
                />
                <Col width={8}>
                  {true && (
                    <Row>
                      <Col
                        width={12}
                        extraStyles="flex items-center gap-2 mb-3"
                      >
                        <AddButton icon onClick={handleAddAnswer} />
                        <span className="text-sm text-[#6b7280]">
                          Add Answer
                        </span>
                      </Col>
                    </Row>
                  )}

                  {formData.answers?.length > 0 && (
                    <TableContainer tweaks="relative mt-0">
                      <TableView>
                        <THeading>
                          <TRow>
                            {false && (
                              <THCol name="id" value="ID" width="w-1" />
                            )}
                            <THCol name="title" value="Answers" />
                            {!true && <THCol />}
                          </TRow>
                        </THeading>
                        <TContent>
                          {formData.answers.map((answer, index) => (
                            <TRow key={index}>
                              {false && (
                                <TColBorderless>
                                  <ReadOnlyField value={answer.id} />
                                </TColBorderless>
                              )}
                              <TColBorderless tweaks="align-middle">
                                {false ? (
                                  <ReadOnlyField value={answer.title} />
                                ) : (
                                  <TextField
                                    name={`title_${index}`}
                                    value={answer.title}
                                    onChange={handleUpdateAnswer}
                                  />
                                )}
                              </TColBorderless>
                              {true && (
                                <TColBorderless
                                  width="w-12"
                                  tweaks="px-0.5 py-0.5 align-middle text-right"
                                >
                                  <DeleteButton
                                    icon
                                    onClick={() => handleDeleteAnswer(index)}
                                  />
                                </TColBorderless>
                              )}
                            </TRow>
                          ))}
                        </TContent>
                      </TableView>
                    </TableContainer>
                  )}
                </Col>
              </Row>
            )}
          </FormData>

          {true ? (
            <FormControls>
              <CancelButton onClick={goBack} />
              {hasAccess(Permissions.Mutation.UpdateQuestion) && (
                <SaveButton onClick={handleSave} loading={isUpdating} />
              )}
            </FormControls>
          ) : (
            <FormControls>
              <CancelButton onClick={goBack} label="Go back" />
            </FormControls>
          )}
        </FormContainer>
      </BlockContainer>
    </PageContainer>
  );
};

export default Question;
